/**
 * Page-level overlay layer.
 *
 * Owns the roving-tabindex contract and keyboard navigation across the
 * overlays on a single page. The DocumentViewer's `renderOverlay` slot
 * receives one of these per page, so a multi-page document stays
 * predictable: arrow keys navigate within the current page, and the
 * viewer's own scroll-container keyboard handlers (Home/End/PgUp/PgDn)
 * continue to work because we stop propagation only for the keys the
 * layer actually consumes.
 *
 * Selection is uncontrolled by default; pass `selectedId` to drive it
 * from outside (e.g. clicking a Fields-panel row highlights the
 * corresponding overlay on the page).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type KeyboardEvent,
} from 'react'

import type { PageDimensions, Rotation } from '../types'
import { OverlayItem } from './OverlayItem'
import { useOverlaySelection } from './useOverlaySelection'
import type {
  OverlayActivateEvent,
  OverlayId,
  OverlayItemModel,
  OverlaySelectEvent,
} from './types'

export interface OverlayLayerProps {
  pageIndex: number
  pageDimensions: PageDimensions
  scale: number
  rotation: Rotation
  items: OverlayItemModel[]
  /**
   * Controlled selection. Omit for uncontrolled (the layer keeps its
   * own selection state).
   */
  selectedId?: OverlayId | null
  initialSelectedId?: OverlayId | null
  onSelect?: (event: OverlaySelectEvent) => void
  onActivate?: (event: OverlayActivateEvent) => void
  /** Read-only layers can't be selected (still rendered + announced). */
  readOnly?: boolean
  /** Optional aria-label override. Defaults to "Page N annotations". */
  ariaLabel?: string
}

export function OverlayLayer({
  pageIndex,
  pageDimensions,
  scale,
  rotation,
  items,
  selectedId: controlledSelectedId,
  initialSelectedId,
  onSelect,
  onActivate,
  readOnly = false,
  ariaLabel,
}: OverlayLayerProps) {
  // Only this page's overlays — a caller passing a flat list across all
  // pages is fine; we filter at the layer.
  const pageItems = useMemo(
    () => items.filter((item) => item.pageIndex === pageIndex),
    [items, pageIndex],
  )

  const selection = useOverlaySelection({
    items: pageItems,
    initialSelectedId: initialSelectedId ?? null,
    selectedId: controlledSelectedId,
    onSelectionChange: (id) => {
      onSelect?.({ id, source: 'programmatic' })
    },
  })

  // Track item refs so the layer can move keyboard focus across overlays
  // imperatively (proper roving-tabindex).
  const itemRefs = useRef<Map<OverlayId, HTMLButtonElement>>(new Map())

  const setItemRef = useCallback(
    (id: OverlayId, el: HTMLButtonElement | null) => {
      if (el) itemRefs.current.set(id, el)
      else itemRefs.current.delete(id)
    },
    [],
  )

  // When the focused id changes, move DOM focus too — but only if focus
  // is already inside the layer. We never steal focus from elsewhere in
  // the page (e.g. a Fields panel input).
  const layerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const id = selection.focusedId
    if (id == null) return
    const layer = layerRef.current
    if (!layer) return
    if (!layer.contains(document.activeElement)) return
    const target = itemRefs.current.get(id)
    target?.focus()
  }, [selection.focusedId])

  const handleSelect = useCallback(
    (event: OverlayActivateEvent) => {
      selection.select(event.id)
      onSelect?.({ id: event.id, source: event.source })
    },
    [onSelect, selection],
  )

  const handleActivate = useCallback(
    (event: OverlayActivateEvent) => {
      onActivate?.(event)
    },
    [onActivate],
  )

  // Keyboard nav within the layer. We deliberately don't preventDefault
  // on keys we don't handle — Home/End/+/-/0 still belong to the viewer
  // scroll container.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) return
    if (event.metaKey || event.ctrlKey || event.altKey) return
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        event.stopPropagation()
        selection.focusNext()
        return
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        event.stopPropagation()
        selection.focusPrev()
        return
      case 'Escape':
        if (selection.selectedId !== null || selection.focusedId !== null) {
          event.preventDefault()
          event.stopPropagation()
          selection.select(null)
          selection.focus(null)
        }
        return
      default:
        return
    }
  }

  // Decide the single tab stop: prefer focused, else selected, else first
  // selectable item. Items without a tab stop carry tabIndex=-1 so the
  // user Tabs *into* the layer once and Arrow-keys through it.
  const tabStopId = useMemo<OverlayId | null>(() => {
    if (selection.focusedId !== null) return selection.focusedId
    if (selection.selectedId !== null) return selection.selectedId
    for (const item of pageItems) {
      if (item.selectable !== false && item.kind !== 'highlight') {
        return item.id
      }
    }
    return null
  }, [selection.focusedId, selection.selectedId, pageItems])

  return (
    <div
      ref={layerRef}
      role="group"
      aria-label={ariaLabel ?? `Page ${pageIndex + 1} annotations`}
      data-testid="viewer-overlay-region"
      data-overlay-page-index={pageIndex}
      onKeyDown={onKeyDown}
      // Inherit pointer-events: none from the parent slot so the page
      // canvas remains "clickable through" the layer. Individual items
      // re-enable pointer events on themselves when interactive.
      className="absolute inset-0"
    >
      {pageItems.map((item) => (
        <OverlayItem
          key={item.id}
          ref={(el) => setItemRef(item.id, el)}
          item={item}
          pageDimensions={pageDimensions}
          scale={scale}
          rotation={rotation}
          selected={selection.isSelected(item.id)}
          focused={selection.isFocused(item.id)}
          tabStop={tabStopId === item.id}
          interactive={!readOnly}
          onSelect={handleSelect}
          onActivate={handleActivate}
          onFocus={selection.focus}
        />
      ))}
    </div>
  )
}
